/**
 * The library read — ADR-033 phase 3, the kernel half (pure data).
 *
 * The packager needs four answers before the panel can offer anything, and
 * every one of them is a question about World's own files, answered by
 * World's own rules ported here so the two sides cannot drift:
 *
 *  1. WHICH FAMILY animates this rig — World's `detectFamily` rule verbatim
 *     (AnimationFamilyManifest.ts): every bone in a family's `bodySignature`
 *     must exist on the skeleton, `*_synth` markers ignored, first family in
 *     the known order that fits wins. Subset, never equality: the signature
 *     omits digits, so a grafted hand carries MORE bones than it lists.
 *     ADR-033 §4: the family id comes from HERE, never from the Document's
 *     `target.family` (a rig-signature family, not a library).
 *  2. WHAT THE FAMILY WOULD PLAY on this avatar before any pin — World's
 *     `resolveBinding` rule verbatim (AnimationSetResolver.ts): candidates
 *     match by key and selector (`*`, or a prefix on the entity path), the
 *     DEEPEST selector wins, ties go to the first in the document, a state
 *     falls back to `neutral`, and `default` is an alias of `idle`.
 *  3. WHICH CLIPS ARE SHIPPED — a bake folder badged against prod (F5): a
 *     clip is `shipped` when prod's manifest carries its clipId, `staged`
 *     when only the bake does. A pin on a staged clip is a pin World cannot
 *     play yet; the panel says so, this seam measures it.
 *  4. WHICH ROSTER ROW THIS IS — Allen's replacement convention: a bench file
 *     that replaces an old row uses the SAME NAME, and the old ids are
 *     `<name>-tripo-handed`, not `slugify(name)`. So the id is pre-filled
 *     from a live roster row with the same name, or a re-package would
 *     append a twin beside the row it meant to replace.
 *
 * Parsers are lenient where World's readers are lenient (a roster row
 * without an id or a file is dropped and counted, never shipped as
 * "[object Object]") and strict where a wrong answer would mislead (a
 * manifest without a signature or clips is not a library).
 *
 * Depends on: zod, ir/errors, session/document (the ADR-189 schemas).
 * Depended on by: the app's library read (fetches, then asks here), the
 * package panel (phase 5), tests.
 */
import { z } from 'zod';
import { SchemaValidationError } from '../ir/errors.js';
import { setBindingSchema, type SetBinding } from './document.js';

// ───────────────────────────── World's three documents ─────────────────────────────

/** ADR-178 ClipRecord, the fields every consumer reads; the rest rides along. */
export const libraryClipRecordSchema = z.looseObject({
  clipId: z.string().min(1),
  family: z.string().min(1),
  group: z.string().min(1),
  action: z.string().optional(),
  variantIndex: z.number().int().nonnegative().optional(),
  durationS: z.number().nonnegative().optional(),
  loopable: z.union([z.boolean(), z.literal('unknown')]).optional(),
});
export type LibraryClipRecord = z.infer<typeof libraryClipRecordSchema>;

/** ADR-178 FamilyManifest — `family.{id, version, bodySignature, contractKeys, clips}`. */
export const familyManifestSchema = z.object({
  family: z.looseObject({
    id: z.string().min(1),
    version: z.string().min(1),
    bodySignature: z.array(z.string().min(1)).min(1),
    contractKeys: z.record(z.string(), z.string()).default({}),
    /** ADR-178 requires at least one clip — an empty or half-written manifest is not a library (Codex P2, #149). */
    clips: z.array(libraryClipRecordSchema).min(1),
  }),
});
export type FamilyManifest = z.infer<typeof familyManifestSchema>;

/** ADR-189 sets document — `{ family, version, sets: SetBinding[] }`. */
export const setsDocSchema = z.looseObject({
  family: z.string().min(1),
  version: z.number().optional(),
  sets: z.array(setBindingSchema),
});
export type SetsDoc = z.infer<typeof setsDocSchema>;

/** One roster row as World's reader keeps it (prebuiltManifest.ts): id + file required, the rest defaulted. */
export interface RosterRow {
  readonly id: string;
  readonly name: string;
  readonly file: string;
  readonly thumbnail: string | null;
  readonly format: string;
  readonly artist: string;
  readonly tags: readonly string[];
  /** Present once the row has been packaged (World's RosterEntry grows; a classic row has none). */
  readonly set?: string;
}

export interface Roster {
  readonly rows: readonly RosterRow[];
  /** Entries World's reader would drop (no string id or file) — counted, never silently gone. */
  readonly dropped: number;
}

function where(w: string, issues: z.ZodError): SchemaValidationError {
  return new SchemaValidationError(
    w,
    issues.issues.map((i) => `${i.path.join('.') || '(root)'}: ${i.message}`),
  );
}

export function parseFamilyManifest(raw: unknown, w = 'FamilyManifest'): FamilyManifest {
  const parsed = familyManifestSchema.safeParse(raw);
  if (!parsed.success) throw where(w, parsed.error);
  return parsed.data;
}

export function parseSetsDoc(raw: unknown, w = 'SetsDoc'): SetsDoc {
  const parsed = setsDocSchema.safeParse(raw);
  if (!parsed.success) throw where(w, parsed.error);
  return parsed.data;
}

/**
 * World's roster reader, ported: a body without an `avatars` array is a
 * failure; a row without a string id or file is dropped and counted; name
 * falls back to the id, format to the extension.
 */
export function parseRoster(raw: unknown, w = 'Roster'): Roster {
  const list = (raw as { avatars?: unknown } | null)?.avatars;
  if (!Array.isArray(list)) throw new SchemaValidationError(w, ['(root): no avatars array']);
  const rows: RosterRow[] = [];
  let dropped = 0;
  for (const entry of list) {
    const a = entry as Record<string, unknown> | null;
    if (a === null || typeof a !== 'object' || typeof a['id'] !== 'string' || a['id'] === '' || typeof a['file'] !== 'string' || a['file'] === '') {
      dropped += 1;
      continue;
    }
    const file = a['file'];
    rows.push({
      id: a['id'],
      name: typeof a['name'] === 'string' && a['name'].trim() !== '' ? a['name'] : a['id'],
      file,
      thumbnail: typeof a['thumbnail'] === 'string' && a['thumbnail'] !== '' ? a['thumbnail'] : null,
      format: typeof a['format'] === 'string' ? a['format'] : file.toLowerCase().endsWith('.vrm') ? 'vrm' : 'glb',
      artist: typeof a['artist'] === 'string' ? a['artist'] : '',
      tags: Array.isArray(a['tags']) ? a['tags'].filter((t): t is string => typeof t === 'string') : [],
      ...(typeof a['set'] === 'string' ? { set: a['set'] } : {}),
    });
  }
  return { rows, dropped };
}

// ───────────────────────────── 1. which family ─────────────────────────────

export interface FamilyCandidate {
  readonly id: string;
  readonly bodySignature: readonly string[];
}

export interface FamilyMatch {
  readonly id: string;
  /** Signature bones the rig carries (the `_synth` markers World ignores are not counted). */
  readonly matched: number;
}

export interface FamilyMiss {
  readonly id: string;
  readonly missing: readonly string[];
  readonly required: number;
}

export type FamilyDetection = { readonly family: FamilyMatch; readonly misses: readonly FamilyMiss[] } | { readonly family: null; readonly misses: readonly FamilyMiss[] };

/** World's exporter-artifact rule (AnimationFamilyManifest.ts `isExporterArtifact`). */
function isExporterArtifact(bone: string): boolean {
  return bone.endsWith('_synth');
}

/**
 * The family this rig belongs to, by World's rule: the first candidate (in
 * the order given — World's `KNOWN_FAMILIES` order) whose every signature
 * bone is present. Session names may still carry `_graft`; the FILE's names
 * do not, so the suffix is stripped before asking. Every miss is returned
 * with what was missing, so a refusal can say "missing 3/41: …".
 */
export function detectLibraryFamily(boneNames: readonly string[], candidates: readonly FamilyCandidate[]): FamilyDetection {
  const present = new Set(boneNames.map((n) => n.replace(/_graft$/, '')));
  const misses: FamilyMiss[] = [];
  for (const fam of candidates) {
    const required = fam.bodySignature.filter((b) => !isExporterArtifact(b));
    const missing = required.filter((b) => !present.has(b));
    if (missing.length === 0) return { family: { id: fam.id, matched: required.length }, misses };
    misses.push({ id: fam.id, missing, required: required.length });
  }
  return { family: null, misses };
}

// ───────────────────────────── 2. what the family would play ─────────────────────────────

/** World's `selectorMatches`: `*`, an exact path, or a prefix on a `/` boundary. */
export function selectorMatches(selector: string, entityPath: string): boolean {
  if (selector === '*') return true;
  return entityPath === selector || entityPath.startsWith(`${selector}/`);
}

/** World's `selectorDepth`: `*` is 0, otherwise the segment count. */
export function selectorDepth(selector: string): number {
  return selector === '*' ? 0 : selector.split('/').length;
}

/** World's `normalizeKey`: ADR-163's `default` and the controller's `idle` are one intent. */
export function normalizeSetKey(key: string): string {
  return key === 'default' ? 'idle' : key;
}

/**
 * World's `resolveBinding`, verbatim in behaviour: candidates by key and
 * selector; within the wanted state the deepest selector wins and a tie goes
 * to the first in the document; a non-neutral state with no candidate falls
 * back to neutral. Null when nothing matches.
 */
export function resolveBinding(doc: SetsDoc, key: string, entityPath: string, state = 'neutral'): SetBinding | null {
  const wanted = normalizeSetKey(key);
  const candidates = doc.sets.filter((s) => normalizeSetKey(s.key) === wanted && selectorMatches(s.selector, entityPath));
  if (candidates.length === 0) return null;
  const pick = (st: string): SetBinding | null => {
    const inState = candidates.filter((s) => (s.state ?? 'neutral') === st);
    let best: SetBinding | null = null;
    for (const s of inState) {
      if (best === null || selectorDepth(s.selector) > selectorDepth(best.selector)) best = s;
    }
    return best;
  };
  return pick(state) ?? (state !== 'neutral' ? pick('neutral') : null);
}

export interface InheritedBinding {
  readonly key: string;
  readonly state: string;
  /** The binding World would resolve for this avatar today (its selector says at which depth). */
  readonly binding: SetBinding;
  /**
   * Where World gets it: a `sets` binding, or the manifest's `contractKeys`
   * fallback (one clip per key, no selector, no state) that World plays when
   * no binding matches the key at this path (Codex P1, #149).
   */
  readonly source: 'sets' | 'contractKeys';
}

/**
 * Every (key, state) World would resolve for THIS avatar before the author
 * pins anything: the sets document's bindings at this entity path, then the
 * manifest's `contractKeys` for every key no binding reached (World's
 * fallback, `lint-animation-sets.mjs` documents it). A pin is an exception
 * to one of these rows; a key with neither source is simply absent.
 */
export function inheritedBindings(doc: SetsDoc | null, entityPath: string, contractKeys: Readonly<Record<string, string>> = {}): InheritedBinding[] {
  const seen = new Set<string>();
  const out: InheritedBinding[] = [];
  for (const s of doc?.sets ?? []) {
    const key = normalizeSetKey(s.key);
    const state = s.state ?? 'neutral';
    const tuple = `${key}@${state}`;
    if (seen.has(tuple)) continue;
    seen.add(tuple);
    const binding = resolveBinding(doc!, key, entityPath, state);
    if (binding !== null) out.push({ key, state, binding, source: 'sets' });
  }
  // World reaches for `contractKeys[key]` when the NEUTRAL resolve is null —
  // a key bound only in `combat` at this path still plays the contract clip
  // in neutral (Codex P1, #149 round two).
  for (const [rawKey, clipId] of Object.entries(contractKeys)) {
    const key = normalizeSetKey(rawKey);
    if (doc !== null && resolveBinding(doc, key, entityPath, 'neutral') !== null) continue;
    out.push({ key, state: 'neutral', binding: { key: rawKey, selector: '*', clips: [clipId] }, source: 'contractKeys' });
  }
  return out;
}

// ───────────────────────────── 3. which clips are shipped ─────────────────────────────

export type ClipShipping = 'shipped' | 'staged';

export interface ClipBadge {
  readonly clipId: string;
  readonly group: string;
  readonly shipping: ClipShipping;
  readonly record: LibraryClipRecord;
}

export interface BadgedLibrary {
  readonly familyId: string;
  readonly bakeVersion: string;
  /** null = prod could not be read; every clip is then badged `staged` and `prodUnknown` says why. */
  readonly prodVersion: string | null;
  readonly prodUnknown: string | null;
  readonly clips: readonly ClipBadge[];
  readonly shipped: number;
  readonly staged: number;
  /** clipIds prod carries that the bake does not — a bake behind prod, or a curated removal. */
  readonly prodOnly: readonly string[];
}

/**
 * The bake badged against prod (F5). The bake is the pool the author pins
 * from; prod is what World can play today. `prodOnly` is the other
 * direction, reported because a bake that lost clips prod still serves is a
 * fact worth a glance, never a refusal.
 */
export function badgeLibrary(bake: FamilyManifest, prod: FamilyManifest | null, prodUnknown: string | null = null): BadgedLibrary {
  if (bake.family.id !== (prod?.family.id ?? bake.family.id)) {
    throw new SchemaValidationError('BadgedLibrary', [`bake is family "${bake.family.id}" but prod is "${prod?.family.id ?? ''}" — badge a bake against its own family`]);
  }
  const prodIds = new Set(prod?.family.clips.map((c) => c.clipId) ?? []);
  const clips: ClipBadge[] = bake.family.clips.map((record) => ({
    clipId: record.clipId,
    group: record.group,
    shipping: prodIds.has(record.clipId) ? 'shipped' : 'staged',
    record,
  }));
  const bakeIds = new Set(bake.family.clips.map((c) => c.clipId));
  const shipped = clips.filter((c) => c.shipping === 'shipped').length;
  return {
    familyId: bake.family.id,
    bakeVersion: bake.family.version,
    prodVersion: prod?.family.version ?? null,
    prodUnknown: prod === null ? (prodUnknown ?? 'prod manifest not read') : null,
    clips,
    shipped,
    staged: clips.length - shipped,
    prodOnly: [...prodIds].filter((id) => !bakeIds.has(id)),
  };
}

// ───────────────────────────── 4. which roster row ─────────────────────────────

export interface RosterMatch {
  readonly row: RosterRow;
  /** How the row was found — the panel says "replaces Amari (amari-tripo-handed)" from this. */
  readonly by: 'id' | 'name';
}

/** Case- and whitespace-insensitive name equality — a display name, not a key. */
function sameName(a: string, b: string): boolean {
  return a.trim().toLocaleLowerCase() === b.trim().toLocaleLowerCase();
}

/**
 * The roster row this package replaces, if any: by the author's chosen id
 * first (a re-package of a packaged avatar), else by the same NAME (Allen's
 * replacement convention for the classic rows). Two rows with one name is a
 * roster defect, refused with both ids rather than guessed.
 */
export function findRosterRow(roster: Roster, choice: { readonly id?: string; readonly name: string }): RosterMatch | null {
  if (choice.id !== undefined) {
    const byId = roster.rows.find((r) => r.id === choice.id);
    if (byId !== undefined) return { row: byId, by: 'id' };
  }
  const byName = roster.rows.filter((r) => sameName(r.name, choice.name));
  if (byName.length > 1) {
    throw new SchemaValidationError('Roster', [
      `"${choice.name}" names ${byName.length} roster rows (${byName.map((r) => r.id).join(', ')}) — the roster must be fixed before a package can replace one of them`,
    ]);
  }
  const row = byName[0];
  return row === undefined ? null : { row, by: 'name' };
}

export interface IdPrefill {
  readonly id: string;
  /** `roster` = a live row with the same name (the slot is kept); `chosen` = the author's own id stands. */
  readonly source: 'roster' | 'chosen';
  readonly replaces: RosterRow | null;
}

/**
 * The id a package should carry: the author's own when set, else the id of
 * the roster row with the same name, else nothing (the composer will
 * `slugify(name)` for a new row). Never overrides a chosen id — the roster
 * informs, the author decides.
 */
export function prefillPackageId(roster: Roster, choice: { readonly id?: string; readonly name: string }): IdPrefill | null {
  const match = findRosterRow(roster, choice);
  if (choice.id !== undefined) return { id: choice.id, source: 'chosen', replaces: match?.row ?? null };
  if (match === null) return null;
  return { id: match.row.id, source: 'roster', replaces: match.row };
}
